import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { invalidateDbTranslations } from '@/lib/i18nDb';

interface Language {
  id: number;
  code: string;
  label: string;
  native_label: string;
  flag: string;
  dir: string;
  active: number;
  is_default: number;
  sort_order: number;
}

interface Translation {
  id: number;
  lang: string;
  namespace: string;
  key: string;
  value: string;
}

const DEFAULT_KEYS = [
  { namespace: 'nav', key: 'home', fr: 'Accueil', en: 'Home', ar: 'الرئيسية', da: 'الدار' },
  { namespace: 'nav', key: 'products', fr: 'Produits', en: 'Products', ar: 'المنتجات', da: 'السلع' },
  { namespace: 'nav', key: 'services', fr: 'Services', en: 'Services', ar: 'الخدمات', da: 'الخدمات' },
  { namespace: 'nav', key: 'contact', fr: 'Contact', en: 'Contact', ar: 'اتصال', da: 'تواصل' },
  { namespace: 'nav', key: 'login', fr: 'Connexion', en: 'Login', ar: 'تسجيل الدخول', da: 'دخول' },
  { namespace: 'nav', key: 'register', fr: 'Inscription', en: 'Register', ar: 'التسجيل', da: 'سجل' },
  { namespace: 'common', key: 'add_to_cart', fr: 'Ajouter au panier', en: 'Add to cart', ar: 'أضف للسلة', da: 'زيد للسلة' },
  { namespace: 'common', key: 'buy_now', fr: 'Acheter maintenant', en: 'Buy now', ar: 'اشتر الآن', da: 'شري دابا' },
  { namespace: 'common', key: 'search', fr: 'Rechercher', en: 'Search', ar: 'بحث', da: 'قلب' },
  { namespace: 'common', key: 'see_more', fr: 'Voir plus', en: 'See more', ar: 'رؤية المزيد', da: 'شوف أكثر' },
  { namespace: 'common', key: 'loading', fr: 'Chargement...', en: 'Loading...', ar: 'جار التحميل...', da: 'تحمل...' },
  { namespace: 'common', key: 'error', fr: 'Erreur', en: 'Error', ar: 'خطأ', da: 'غلطة' },
  { namespace: 'common', key: 'save', fr: 'Enregistrer', en: 'Save', ar: 'حفظ', da: 'حفظ' },
  { namespace: 'common', key: 'cancel', fr: 'Annuler', en: 'Cancel', ar: 'إلغاء', da: 'بطل' },
  { namespace: 'common', key: 'confirm', fr: 'Confirmer', en: 'Confirm', ar: 'تأكيد', da: 'أكد' },
  { namespace: 'common', key: 'delete', fr: 'Supprimer', en: 'Delete', ar: 'حذف', da: 'حيد' },
  { namespace: 'common', key: 'edit', fr: 'Modifier', en: 'Edit', ar: 'تعديل', da: 'عدل' },
  { namespace: 'store', key: 'empty_cart', fr: 'Votre panier est vide', en: 'Your cart is empty', ar: 'سلتك فارغة', da: 'السلة خاوية' },
  { namespace: 'store', key: 'order_confirmed', fr: 'Commande confirmée', en: 'Order confirmed', ar: 'تم تأكيد الطلب', da: 'الطلب تأكد' },
  { namespace: 'store', key: 'out_of_stock', fr: 'Rupture de stock', en: 'Out of stock', ar: 'نفد المخزون', da: 'ماعادش موجود' },
  { namespace: 'credit', key: 'title', fr: 'Zifek Crédit', en: 'Zifek Credit', ar: 'زيفيك الدين', da: 'دفتر الدين' },
  { namespace: 'credit', key: 'add_credit', fr: 'Ajouter un crédit', en: 'Add credit', ar: 'إضافة دين', da: 'زيد دين' },
  { namespace: 'credit', key: 'record_payment', fr: 'Enregistrer un paiement', en: 'Record payment', ar: 'تسجيل الدفع', da: 'سجل تسديد' },
  { namespace: 'credit', key: 'balance', fr: 'Solde', en: 'Balance', ar: 'الرصيد', da: 'الرصيد' },
  // ── Navigation du dashboard marchand ──
  { namespace: 'dash', key: 'dashboard', fr: 'Tableau de bord', en: 'Dashboard', ar: 'لوحة التحكم', da: 'لوحة التحكم' },
  { namespace: 'dash', key: 'products', fr: 'Produits', en: 'Products', ar: 'المنتجات', da: 'السلع' },
  { namespace: 'dash', key: 'caisse', fr: 'Ma caisse', en: 'My register', ar: 'الصندوق', da: 'الكاسة' },
  { namespace: 'dash', key: 'services', fr: 'Services', en: 'Services', ar: 'الخدمات', da: 'الخدمات' },
  { namespace: 'dash', key: 'orders', fr: 'Commandes', en: 'Orders', ar: 'الطلبات', da: 'الطلبات' },
  { namespace: 'dash', key: 'customers', fr: 'Clients', en: 'Customers', ar: 'العملاء', da: 'الكليان' },
  { namespace: 'dash', key: 'credit', fr: 'Crédit', en: 'Credit', ar: 'الدين', da: 'الدين' },
  { namespace: 'dash', key: 'payments', fr: 'Paiements', en: 'Payments', ar: 'المدفوعات', da: 'الأداء' },
  { namespace: 'dash', key: 'wallet', fr: 'Portefeuille', en: 'Wallet', ar: 'المحفظة', da: 'البورتفاي' },
  { namespace: 'dash', key: 'store', fr: 'Ma boutique', en: 'My store', ar: 'متجري', da: 'الحانوت' },
  { namespace: 'dash', key: 'site_content', fr: 'Contenu du site', en: 'Site content', ar: 'محتوى الموقع', da: 'محتوى السيت' },
  { namespace: 'dash', key: 'media', fr: 'Médias', en: 'Media', ar: 'الوسائط', da: 'الميديا' },
  { namespace: 'dash', key: 'documents', fr: 'Documents', en: 'Documents', ar: 'الوثائق', da: 'الوثائق' },
  { namespace: 'dash', key: 'storage', fr: 'Stockage', en: 'Storage', ar: 'التخزين', da: 'التخزين' },
  { namespace: 'dash', key: 'analytics', fr: 'Statistiques', en: 'Analytics', ar: 'الإحصائيات', da: 'الإحصائيات' },
  { namespace: 'dash', key: 'appstore', fr: 'AppStore', en: 'AppStore', ar: 'متجر التطبيقات', da: 'متجر التطبيقات' },
  { namespace: 'dash', key: 'apps', fr: 'Mes Apps', en: 'My apps', ar: 'تطبيقاتي', da: 'التطبيقات' },
  { namespace: 'dash', key: 'themestore', fr: 'ThemeStore', en: 'ThemeStore', ar: 'متجر القوالب', da: 'متجر القوالب' },
  { namespace: 'dash', key: 'settings', fr: 'Paramètres', en: 'Settings', ar: 'الإعدادات', da: 'الإعدادات' },
  // ── Sélecteur de langue ──
  { namespace: 'lang', key: 'change', fr: 'Changer de langue', en: 'Change language', ar: 'تغيير اللغة', da: 'بدل اللغة' },
  // ── Page d'accueil Zifek ──
  { namespace: 'home', key: 'nav_features', fr: 'Fonctionnalités', en: 'Features', ar: 'الميزات', da: 'الخدمات' },
  { namespace: 'home', key: 'nav_how', fr: 'Comment ça marche', en: 'How it works', ar: 'كيف يعمل', da: 'كيفاش كيخدم' },
  { namespace: 'home', key: 'nav_pricing', fr: 'Tarifs', en: 'Pricing', ar: 'الأسعار', da: 'الثمن' },
  { namespace: 'home', key: 'nav_faq', fr: 'FAQ', en: 'FAQ', ar: 'الأسئلة الشائعة', da: 'أسئلة' },
  { namespace: 'home', key: 'create_site', fr: 'Créer mon site', en: 'Create my site', ar: 'أنشئ موقعي', da: 'دير السيت ديالي' },
  { namespace: 'home', key: 'my_space', fr: 'Mon espace Zifek', en: 'My Zifek space', ar: 'مساحتي في زيفيك', da: 'الفضاء ديالي' },
  { namespace: 'home', key: 'logout', fr: 'Déconnexion', en: 'Log out', ar: 'تسجيل الخروج', da: 'خرج' },
  // ── Connexion ──
  { namespace: 'auth', key: 'login', fr: 'Connexion', en: 'Login', ar: 'تسجيل الدخول', da: 'دخول' },
  { namespace: 'auth', key: 'register', fr: 'Inscription', en: 'Sign up', ar: 'إنشاء حساب', da: 'تسجيل' },
  { namespace: 'auth', key: 'client_space', fr: 'Espace client', en: 'Client area', ar: 'فضاء العميل', da: 'فضاء الكليان' },
  { namespace: 'auth', key: 'pro_space', fr: 'Espace professionnel', en: 'Business area', ar: 'الفضاء المهني', da: 'فضاء المهني' },
  // ── Navigation SuperAdmin ──
  { namespace: 'sa', key: 'dashboard', fr: 'Tableau de bord', en: 'Dashboard', ar: 'لوحة التحكم', da: 'لوحة التحكم' },
  { namespace: 'sa', key: 'users', fr: 'Utilisateurs', en: 'Users', ar: 'المستخدمون', da: 'المستعملين' },
  { namespace: 'sa', key: 'themes', fr: 'Theme Builder', en: 'Theme Builder', ar: 'منشئ القوالب', da: 'بانّي القوالب' },
  { namespace: 'sa', key: 'apps', fr: 'App Builder', en: 'App Builder', ar: 'منشئ التطبيقات', da: 'بانّي التطبيقات' },
  { namespace: 'sa', key: 'homepage', fr: "Page d'accueil", en: 'Homepage', ar: 'الصفحة الرئيسية', da: 'الصفحة الرئيسية' },
  { namespace: 'sa', key: 'translations', fr: 'Langues & Traductions', en: 'Languages & Translations', ar: 'اللغات والترجمات', da: 'اللغات والترجمات' },
  { namespace: 'sa', key: 'support', fr: 'Support client', en: 'Customer support', ar: 'دعم العملاء', da: 'دعم الكليان' },
  { namespace: 'sa', key: 'logs', fr: 'Logs Système', en: 'System logs', ar: 'سجلات النظام', da: 'السجلات' },
  { namespace: 'sa', key: 'storage', fr: 'Stockage', en: 'Storage', ar: 'التخزين', da: 'التخزين' },
  { namespace: 'sa', key: 'settings', fr: 'Paramètres IA', en: 'AI settings', ar: 'إعدادات الذكاء الاصطناعي', da: 'إعدادات الذكاء' },
  // ── Support ──
  { namespace: 'support', key: 'title', fr: 'Service Client & Support', en: 'Customer Service & Support', ar: 'خدمة العملاء والدعم', da: 'خدمة الكليان والدعم' },
  { namespace: 'support', key: 'new_ticket', fr: 'Nouveau ticket', en: 'New ticket', ar: 'تذكرة جديدة', da: 'تيكي جديد' },
  { namespace: 'support', key: 'subject', fr: 'Sujet', en: 'Subject', ar: 'الموضوع', da: 'الموضوع' },
  { namespace: 'support', key: 'message', fr: 'Message', en: 'Message', ar: 'الرسالة', da: 'الرسالة' },
  { namespace: 'support', key: 'reply', fr: 'Répondre', en: 'Reply', ar: 'رد', da: 'رد' },
  { namespace: 'support', key: 'open', fr: 'Ouvert', en: 'Open', ar: 'مفتوح', da: 'مفتوح' },
  { namespace: 'support', key: 'in_progress', fr: 'En cours', en: 'In progress', ar: 'قيد المعالجة', da: 'كيتعالج' },
  { namespace: 'support', key: 'resolved', fr: 'Résolu', en: 'Resolved', ar: 'تم الحل', da: 'تحل' },
  { namespace: 'support', key: 'contact_team', fr: "Contacter l'équipe Zifek", en: 'Contact the Zifek team', ar: 'التواصل مع فريق زيفيك', da: 'تواصل مع فريق زيفيك' },
  // ── Dashboard — compte ──
  { namespace: 'dash', key: 'account', fr: 'Mon compte', en: 'My account', ar: 'حسابي', da: 'الحساب ديالي' },
  { namespace: 'dash', key: 'profile', fr: 'Mon profil', en: 'My profile', ar: 'ملفي', da: 'البروفيل' },
  { namespace: 'dash', key: 'install_app', fr: "Installer l'app", en: 'Install app', ar: 'تثبيت التطبيق', da: 'ثبّت التطبيق' },
  { namespace: 'dash', key: 'my_qr', fr: 'Mon QR Zifek', en: 'My Zifek QR', ar: 'رمز QR الخاص بي', da: 'الـ QR ديالي' },
  { namespace: 'dash', key: 'logout', fr: 'Déconnexion', en: 'Log out', ar: 'تسجيل الخروج', da: 'خرج' },
  // ── Pages métier : Crédit ──
  { namespace: 'credit', key: 'subtitle', fr: 'Votre carnet de crédit numérique', en: 'Your digital credit ledger', ar: 'دفتر الدين الرقمي الخاص بك', da: 'الدفتر الرقمي ديال الدين' },
  { namespace: 'credit', key: 'tab.carnet', fr: 'Carnet', en: 'Ledger', ar: 'الدفتر', da: 'الدفتر' },
  { namespace: 'credit', key: 'tab.clients', fr: 'Clients', en: 'Customers', ar: 'العملاء', da: 'الكليان' },
  { namespace: 'credit', key: 'tab.rapports', fr: 'Rapports', en: 'Reports', ar: 'التقارير', da: 'التقارير' },
  { namespace: 'credit', key: 'tab.reglages', fr: 'Réglages', en: 'Settings', ar: 'الإعدادات', da: 'الإعدادات' },
  { namespace: 'credit', key: 'photo_debts', fr: 'Photo dettes', en: 'Debt photo', ar: 'صورة الديون', da: 'صورة الديون' },
  { namespace: 'credit', key: 'add_credit_btn', fr: 'Ajouter un crédit', en: 'Add credit', ar: 'إضافة دين', da: 'زيد دين' },
  { namespace: 'credit', key: 'search_client', fr: 'Rechercher un client...', en: 'Search a customer...', ar: 'ابحث عن عميل...', da: 'قلّب على كليان...' },
  { namespace: 'credit', key: 'enable_reminders', fr: 'Activez les rappels de crédit', en: 'Enable credit reminders', ar: 'فعّل تذكيرات الدين', da: 'فعّل التذكير بالدين' },
  { namespace: 'cr', key: 'qa_payment', fr: 'Enregistrer un paiement', en: 'Record a payment', ar: 'تسجيل الدفع', da: 'سجّل تسديد' },
  { namespace: 'cr', key: 'qa_add_client', fr: 'Ajouter un client', en: 'Add a customer', ar: 'إضافة عميل', da: 'زيد كليان' },
  { namespace: 'cr', key: 'qa_import_photo', fr: 'Importer par photo', en: 'Import from photo', ar: 'استيراد من صورة', da: 'دخّل من صورة' },
  { namespace: 'cr', key: 'qa_invite', fr: 'Inviter un client', en: 'Invite a customer', ar: 'دعوة عميل', da: 'عيّط كليان' },
  { namespace: 'cr', key: 'qa_statement', fr: 'Générer un relevé', en: 'Generate a statement', ar: 'إنشاء كشف حساب', da: 'دير كشف الحساب' },
  { namespace: 'cr', key: 'qa_carnet', fr: 'Carnet complet', en: 'Full ledger', ar: 'الدفتر الكامل', da: 'الدفتر كامل' },
  // ── Pages métier : Caisse ──
  { namespace: 'caisse', key: 'title', fr: 'Ma caisse', en: 'My register', ar: 'الصندوق', da: 'الكاسة ديالي' },
  { namespace: 'caisse', key: 'subtitle', fr: 'Caisse enregistreuse', en: 'Point of sale', ar: 'آلة تسجيل النقد', da: 'ماكينة الكاسة' },
  { namespace: 'caisse', key: 'search', fr: 'Rechercher un produit...', en: 'Search a product...', ar: 'ابحث عن منتج...', da: 'قلّب على سلعة...' },
  { namespace: 'caisse', key: 'cart', fr: 'Panier', en: 'Cart', ar: 'السلة', da: 'الپانية' },
  { namespace: 'caisse', key: 'empty', fr: 'Panier vide', en: 'Empty cart', ar: 'السلة فارغة', da: 'الپانية خاوية' },
  { namespace: 'caisse', key: 'checkout', fr: 'Encaisser', en: 'Checkout', ar: 'تحصيل', da: 'خود الفلوس' },
  { namespace: 'caisse', key: 'new_sale', fr: 'Nouvelle vente', en: 'New sale', ar: 'بيع جديد', da: 'بيع جديد' },
  { namespace: 'caisse', key: 'cash', fr: 'Espèces', en: 'Cash', ar: 'نقدا', da: 'كاش' },
  { namespace: 'caisse', key: 'card', fr: 'Carte', en: 'Card', ar: 'بطاقة', da: 'كارط' },
  // ── Pages métier : Produits & Rapports ──
  { namespace: 'products', key: 'title', fr: 'Produits', en: 'Products', ar: 'المنتجات', da: 'السلع' },
  { namespace: 'products', key: 'subtitle', fr: 'Gérez votre catalogue de produits', en: 'Manage your product catalog', ar: 'أدر كتالوج منتجاتك', da: 'سيّر الكاطالوغ ديال السلع' },
  { namespace: 'products', key: 'categories', fr: 'Catégories', en: 'Categories', ar: 'الفئات', da: 'الفئات' },
  { namespace: 'products', key: 'catalog', fr: 'Catalogue', en: 'Catalog', ar: 'الكتالوج', da: 'الكاطالوغ' },
  { namespace: 'products', key: 'stock_mgmt', fr: 'Gestion de stock', en: 'Stock management', ar: 'إدارة المخزون', da: 'تدبير المخزون' },
  { namespace: 'products', key: 'search', fr: 'Rechercher un produit...', en: 'Search a product...', ar: 'ابحث عن منتج...', da: 'قلّب على سلعة...' },
  { namespace: 'reports', key: 'title', fr: 'Rapports', en: 'Reports', ar: 'التقارير', da: 'التقارير' },
  // ── Devise ──
  { namespace: 'currency', key: 'change', fr: 'Changer de devise', en: 'Change currency', ar: 'تغيير العملة', da: 'بدّل العملة' },
  { namespace: 'currency', key: 'equiv', fr: 'équivaut à', en: 'equals', ar: 'يساوي', da: 'كيسوى' },
  // ── Commandes ──
  { namespace: 'orders', key: 'title', fr: 'Commandes', en: 'Orders', ar: 'الطلبات', da: 'الطلبات' },
  { namespace: 'orders', key: 'subtitle', fr: 'Suivez et gérez toutes les commandes', en: 'Track and manage all orders', ar: 'تابع وأدر جميع الطلبات', da: 'تبّع وسيّر گاع الطلبات' },
  { namespace: 'orders', key: 'filter_all', fr: 'Toutes', en: 'All', ar: 'الكل', da: 'الكل' },
  { namespace: 'orders', key: 'filter_pending', fr: 'En attente', en: 'Pending', ar: 'قيد الانتظار', da: 'كيتسنى' },
  { namespace: 'orders', key: 'filter_paid', fr: 'Payées', en: 'Paid', ar: 'مدفوعة', da: 'مخلّصة' },
  { namespace: 'orders', key: 'filter_processing', fr: 'Traitement', en: 'Processing', ar: 'قيد المعالجة', da: 'كيتعالج' },
  { namespace: 'orders', key: 'filter_shipped', fr: 'Expédiées', en: 'Shipped', ar: 'تم الشحن', da: 'تصيفطت' },
  { namespace: 'orders', key: 'filter_delivered', fr: 'Livrées', en: 'Delivered', ar: 'تم التسليم', da: 'توصلت' },
  { namespace: 'orders', key: 'empty', fr: 'Aucune commande', en: 'No orders', ar: 'لا توجد طلبات', da: 'مكاينش طلبات' },
  { namespace: 'orders', key: 'empty_hint', fr: 'Les commandes apparaîtront ici quand vous en recevrez', en: 'Orders will appear here when you receive them', ar: 'ستظهر الطلبات هنا عند استلامها', da: 'الطلبات غادي تبان هنا منين تجيك' },
  { namespace: 'orders', key: 'items', fr: 'Articles', en: 'Items', ar: 'العناصر', da: 'السلع' },
  { namespace: 'orders', key: 'subtotal', fr: 'Sous-total', en: 'Subtotal', ar: 'المجموع الفرعي', da: 'المجموع الجزئي' },
  { namespace: 'orders', key: 'shipping', fr: 'Livraison', en: 'Shipping', ar: 'التوصيل', da: 'التوصيل' },
  { namespace: 'orders', key: 'tax', fr: 'Taxes', en: 'Taxes', ar: 'الضرائب', da: 'الضرائب' },
  { namespace: 'orders', key: 'discount', fr: 'Remise', en: 'Discount', ar: 'الخصم', da: 'التخفيض' },
  { namespace: 'orders', key: 'total', fr: 'Total', en: 'Total', ar: 'المجموع', da: 'المجموع' },
  { namespace: 'orders', key: 'status', fr: 'Statut', en: 'Status', ar: 'الحالة', da: 'الحالة' },
  { namespace: 'orders', key: 'payment', fr: 'Paiement', en: 'Payment', ar: 'الدفع', da: 'الأداء' },
  // ── Paiements ──
  { namespace: 'payments', key: 'title', fr: 'Paiements', en: 'Payments', ar: 'المدفوعات', da: 'الأداء' },
  { namespace: 'payments', key: 'subtitle', fr: 'Historique des transactions et revenus', en: 'Transaction and revenue history', ar: 'سجل المعاملات والإيرادات', da: 'تاريخ المعاملات والمداخيل' },
  { namespace: 'payments', key: 'received', fr: 'Reçus', en: 'Received', ar: 'مستلمة', da: 'توصلت' },
  { namespace: 'payments', key: 'sent', fr: 'Envoyés', en: 'Sent', ar: 'مرسلة', da: 'صيفطت' },
  { namespace: 'payments', key: 'balance', fr: 'Solde actuel', en: 'Current balance', ar: 'الرصيد الحالي', da: 'الرصيد الحالي' },
  { namespace: 'payments', key: 'filter_all', fr: 'Toutes', en: 'All', ar: 'الكل', da: 'الكل' },
  { namespace: 'payments', key: 'in', fr: 'Reçu', en: 'Received', ar: 'مستلمة', da: 'توصلت' },
  { namespace: 'payments', key: 'out', fr: 'Envoyé', en: 'Sent', ar: 'مرسلة', da: 'صيفطت' },
  // ── Portefeuille ──
  { namespace: 'wallet', key: 'title', fr: 'Portefeuille', en: 'Wallet', ar: 'المحفظة', da: 'البورتفاي' },
  { namespace: 'wallet', key: 'available', fr: 'Solde disponible', en: 'Available balance', ar: 'الرصيد المتاح', da: 'الرصيد المتوفر' },
  { namespace: 'wallet', key: 'recharge', fr: 'Recharger le portefeuille', en: 'Top up wallet', ar: 'إعادة تعبئة المحفظة', da: 'عمّر البورتفاي' },
  { namespace: 'wallet', key: 'history', fr: 'Historique des opérations', en: 'Transaction history', ar: 'سجل العمليات', da: 'تاريخ العمليات' },
  { namespace: 'wallet', key: 'empty', fr: 'Aucune opération pour le moment.', en: 'No transactions yet.', ar: 'لا توجد عمليات بعد.', da: 'مكاينش عمليات دابا.' },
  // ── Paramètres ──
  { namespace: 'settings', key: 'title', fr: 'Paramètres', en: 'Settings', ar: 'الإعدادات', da: 'الإعدادات' },
  { namespace: 'settings', key: 'subtitle', fr: 'Gérez votre compte et vos préférences', en: 'Manage your account and preferences', ar: 'أدر حسابك وتفضيلاتك', da: 'سيّر الحساب والتفضيلات ديالك' },
  { namespace: 'settings', key: 'tab_profile', fr: 'Profil', en: 'Profile', ar: 'الملف الشخصي', da: 'البروفيل' },
  { namespace: 'settings', key: 'tab_password', fr: 'Mot de passe', en: 'Password', ar: 'كلمة المرور', da: 'كلمة السر' },
  { namespace: 'settings', key: 'tab_domain', fr: 'Domaine', en: 'Domain', ar: 'النطاق', da: 'الدومين' },
  { namespace: 'settings', key: 'tab_appearance', fr: 'Apparence', en: 'Appearance', ar: 'المظهر', da: 'المظهر' },
  { namespace: 'settings', key: 'tab_danger', fr: 'Zone danger', en: 'Danger zone', ar: 'منطقة الخطر', da: 'منطقة الخطر' },
];

type TabMode = 'languages' | 'translations';

export default function TranslationsPage() {
  const [tab, setTab] = useState<TabMode>('languages');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // New language form
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newNative, setNewNative] = useState('');
  const [newFlag, setNewFlag] = useState('');
  const [newDir, setNewDir] = useState<'ltr' | 'rtl'>('ltr');
  const [addingLang, setAddingLang] = useState(false);
  const [showAddLang, setShowAddLang] = useState(false);

  // Translation editing
  const [selectedLang, setSelectedLang] = useState('fr');
  const [filterNS, setFilterNS] = useState('all');
  const [searchKey, setSearchKey] = useState('');
  const [edits, setEdits] = useState<Record<string, string>>({});

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [langRes, trRes] = await Promise.all([
        supabase.from('languages').select('*').order('sort_order'),
        supabase.from('translations').select('*').order('namespace').order('key'),
      ]);
      if (langRes.error) throw langRes.error;
      setLanguages(langRes.data || []);
      setTranslations(trRes.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Merge DB translations with DEFAULT_KEYS so every key always shows
  const mergedKeys = useMemo(() => {
    const dbMap: Record<string, Translation> = {};
    translations.forEach((t) => { if (t.lang === selectedLang) dbMap[`${t.namespace}::${t.key}`] = t; });
    return DEFAULT_KEYS.map((dk) => {
      const dbEntry = dbMap[`${dk.namespace}::${dk.key}`];
      return {
        namespace: dk.namespace,
        key: dk.key,
        id: dbEntry?.id ?? null,
        value: edits[`${dk.namespace}::${dk.key}`] ?? dbEntry?.value ?? (dk as Record<string, string>)[selectedLang] ?? dk.fr,
      };
    });
  }, [translations, selectedLang, edits]);

  const namespaces = useMemo(() => ['all', ...Array.from(new Set(DEFAULT_KEYS.map((k) => k.namespace)))], []);

  const filteredKeys = useMemo(() => {
    return mergedKeys.filter((k) => {
      const nsOk = filterNS === 'all' || k.namespace === filterNS;
      const searchOk = !searchKey.trim() || k.key.toLowerCase().includes(searchKey.toLowerCase()) || k.value.toLowerCase().includes(searchKey.toLowerCase());
      return nsOk && searchOk;
    });
  }, [mergedKeys, filterNS, searchKey]);

  const handleEdit = (namespace: string, key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [`${namespace}::${key}`]: value }));
  };

  const saveTranslations = async () => {
    if (Object.keys(edits).length === 0) return;
    setSaving(true);
    try {
      const toUpsert = Object.entries(edits).map(([composite, value]) => {
        const [namespace, key] = composite.split('::');
        return { lang: selectedLang, namespace, key, value, updated_at: new Date().toISOString() };
      });
      const { error: upsertErr } = await supabase
        .from('translations')
        .upsert(toUpsert, { onConflict: 'lang,namespace,key' });
      if (upsertErr) throw upsertErr;
      setEdits({});
      invalidateDbTranslations(selectedLang);
      await load();
      showToast('Traductions enregistrées');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const seedDefaultTranslations = async () => {
    setSaving(true);
    try {
      const rows = languages.flatMap((lang) =>
        DEFAULT_KEYS.map((dk) => ({
          lang: lang.code,
          namespace: dk.namespace,
          key: dk.key,
          value: (dk as Record<string, string>)[lang.code] ?? dk.fr,
        }))
      );
      const { error: upsertErr } = await supabase
        .from('translations')
        .upsert(rows, { onConflict: 'lang,namespace,key' });
      if (upsertErr) throw upsertErr;
      invalidateDbTranslations();
      await load();
      showToast('Traductions par défaut initialisées');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const toggleLangActive = async (lang: Language) => {
    const newActive = lang.active === 1 ? 0 : 1;
    await supabase.from('languages').update({ active: newActive }).eq('id', lang.id);
    setLanguages((prev) => prev.map((l) => l.id === lang.id ? { ...l, active: newActive } : l));
  };

  const setDefaultLang = async (lang: Language) => {
    await Promise.all([
      supabase.from('languages').update({ is_default: 0 }).neq('id', lang.id),
      supabase.from('languages').update({ is_default: 1 }).eq('id', lang.id),
    ]);
    setLanguages((prev) => prev.map((l) => ({ ...l, is_default: l.id === lang.id ? 1 : 0 })));
    showToast(`${lang.label} est maintenant la langue par défaut`);
  };

  const addLanguage = async () => {
    if (!newCode.trim() || !newLabel.trim()) return;
    setAddingLang(true);
    try {
      const { error: insertErr } = await supabase.from('languages').insert({
        code: newCode.trim().toLowerCase(),
        label: newLabel.trim(),
        native_label: newNative.trim() || newLabel.trim(),
        flag: newFlag.trim(),
        dir: newDir,
        active: 1,
        is_default: 0,
        sort_order: languages.length + 1,
      });
      if (insertErr) throw insertErr;
      setNewCode(''); setNewLabel(''); setNewNative(''); setNewFlag(''); setNewDir('ltr');
      setShowAddLang(false);
      await load();
      showToast('Langue ajoutée');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setAddingLang(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Langues & Traductions</h2>
          <p className="text-sm text-foreground-500 mt-1">Gérez les langues de la plateforme et modifiez chaque texte</p>
        </div>
        <button onClick={seedDefaultTranslations} disabled={saving}
          className="flex items-center gap-2 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
          <i className="ri-refresh-line"></i>Initialiser les clés par défaut
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-6">
        {[{ key: 'languages', label: 'Langues', icon: 'ri-global-line' }, { key: 'translations', label: 'Traductions', icon: 'ri-translate' }].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key as TabMode)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === t.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center py-16 text-center">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-sm text-foreground-600 mb-3">{error}</p>
          <button onClick={load} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600">Réessayer</button>
        </div>
      ) : tab === 'languages' ? (
        <div className="space-y-4">
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-3 bg-background-100 border-b border-background-200/70 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">
              <div className="col-span-2">Code</div>
              <div className="col-span-2">Drapeau</div>
              <div className="col-span-3">Langue</div>
              <div className="col-span-2">Sens</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            <div className="divide-y divide-background-200/70">
              {languages.map((lang) => (
                <div key={lang.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 px-5 py-4 items-center">
                  <div className="sm:col-span-2">
                    <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-700 text-xs font-mono font-medium">{lang.code}</span>
                  </div>
                  <div className="sm:col-span-2 text-2xl">{lang.flag}</div>
                  <div className="sm:col-span-3">
                    <div className="text-sm font-semibold text-foreground-900">{lang.label}</div>
                    <div className="text-xs text-foreground-500">{lang.native_label}</div>
                    {lang.is_default === 1 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary-100 text-primary-700 mt-1">Par défaut</span>}
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-xs text-foreground-500">{lang.dir === 'rtl' ? '← Droite à gauche' : 'Gauche à droite →'}</span>
                  </div>
                  <div className="sm:col-span-3 flex flex-wrap items-center gap-1.5 sm:justify-end">
                    {lang.is_default !== 1 && (
                      <button onClick={() => setDefaultLang(lang)}
                        className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                        Définir par défaut
                      </button>
                    )}
                    <button onClick={() => { setSelectedLang(lang.code); setTab('translations'); }}
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap">
                      Traduire
                    </button>
                    <button onClick={() => toggleLangActive(lang)}
                      className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${lang.active === 1 ? 'bg-primary-500' : 'bg-background-200'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${lang.active === 1 ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add language */}
          {!showAddLang ? (
            <button onClick={() => setShowAddLang(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-foreground-950 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer">
              <i className="ri-add-line"></i>Ajouter une langue
            </button>
          ) : (
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-foreground-900 mb-4">Nouvelle langue</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Code (ex: es)</label>
                  <input type="text" value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="es"
                    className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Nom français</label>
                  <input type="text" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Espagnol"
                    className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Nom natif</label>
                  <input type="text" value={newNative} onChange={(e) => setNewNative(e.target.value)} placeholder="Español"
                    className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Emoji drapeau</label>
                  <input type="text" value={newFlag} onChange={(e) => setNewFlag(e.target.value)} placeholder="🇪🇸"
                    className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Sens d'écriture</label>
                  <select value={newDir} onChange={(e) => setNewDir(e.target.value as 'ltr' | 'rtl')}
                    className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300">
                    <option value="ltr">Gauche à droite (LTR)</option>
                    <option value="rtl">Droite à gauche (RTL)</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={addLanguage} disabled={addingLang || !newCode.trim() || !newLabel.trim()}
                  className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50">
                  {addingLang ? <><i className="ri-loader-4-line animate-spin mr-1"></i>Ajout...</> : 'Ajouter'}
                </button>
                <button onClick={() => setShowAddLang(false)}
                  className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Translations tab */
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-foreground-600 whitespace-nowrap">Langue :</label>
              <select value={selectedLang} onChange={(e) => { setSelectedLang(e.target.value); setEdits({}); }}
                className="h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300">
                {languages.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-foreground-600 whitespace-nowrap">Section :</label>
              <select value={filterNS} onChange={(e) => setFilterNS(e.target.value)}
                className="h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300">
                {namespaces.map((ns) => <option key={ns} value={ns}>{ns === 'all' ? 'Toutes' : ns}</option>)}
              </select>
            </div>
            <div className="relative flex-1 max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
              <input type="text" value={searchKey} onChange={(e) => setSearchKey(e.target.value)} placeholder="Rechercher une clé..."
                className="w-full pl-9 pr-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
            </div>
            {Object.keys(edits).length > 0 && (
              <button onClick={saveTranslations} disabled={saving}
                className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                Enregistrer ({Object.keys(edits).length})
              </button>
            )}
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-3 bg-background-100 border-b border-background-200/70 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">
              <div className="col-span-2">Section</div>
              <div className="col-span-3">Clé</div>
              <div className="col-span-7">Traduction</div>
            </div>
            <div className="divide-y divide-background-200/70">
              {filteredKeys.length === 0 ? (
                <div className="py-12 text-center text-sm text-foreground-400">Aucune clé trouvée</div>
              ) : filteredKeys.map((item) => {
                const isEdited = edits[`${item.namespace}::${item.key}`] !== undefined;
                return (
                  <div key={`${item.namespace}::${item.key}`} className={`grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 px-5 py-3 items-center ${isEdited ? 'bg-primary-50/40' : ''}`}>
                    <div className="sm:col-span-2">
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[11px] font-medium">{item.namespace}</span>
                    </div>
                    <div className="sm:col-span-3">
                      <span className="text-xs font-mono text-foreground-700">{item.key}</span>
                      {isEdited && <span className="ml-1.5 text-[10px] text-primary-600 font-medium">• modifié</span>}
                    </div>
                    <div className="sm:col-span-7">
                      <input
                        type="text"
                        value={item.value}
                        onChange={(e) => handleEdit(item.namespace, item.key, e.target.value)}
                        dir={languages.find((l) => l.code === selectedLang)?.dir || 'ltr'}
                        className="w-full h-9 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-medium whitespace-nowrap">
            <i className="ri-check-line text-accent-300"></i>{toast}
          </div>
        </div>
      )}
    </div>
  );
}